import { useEffect, useState } from "react";
import { Check, Eye, EyeOff, LockKeyhole } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "./firebase-config";
import AuthRecoveryShell from "./components/auth/AuthRecoveryShell";

function getResetErrorMessage(error) {
  if (error?.code === "auth/expired-action-code") return "Ce lien a expiré. Demandez un nouveau lien de réinitialisation.";
  if (error?.code === "auth/invalid-action-code") return "Ce lien n'est plus valide. Il a peut-être déjà été utilisé.";
  if (error?.code === "auth/weak-password") return "Ce mot de passe ne respecte pas les règles de sécurité de votre compte.";
  if (error?.code === "auth/network-request-failed") return "La connexion semble interrompue. Vérifiez votre réseau puis réessayez.";
  return "La mise à jour n'a pas pu être terminée. Réessayez dans un instant.";
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [error, setError] = useState("");
  const [linkError, setLinkError] = useState("");
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  useEffect(() => {
    let active = true;

    if (mode !== "resetPassword" || !oobCode) {
      setLinkError("Ce lien de réinitialisation est incomplet ou invalide.");
      setChecking(false);
      return undefined;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        if (!active) return;
        setAccountEmail(email);
        setChecking(false);
      })
      .catch((error) => {
        if (!active) return;
        setLinkError(getResetErrorMessage(error));
        setChecking(false);
      });

    return () => {
      active = false;
    };
  }, [mode, oobCode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }

    try {
      setLoading(true);
      await confirmPasswordReset(auth, oobCode, password);
      setDone(true);
    } catch (error) {
      console.error("Password reset confirmation error:", error);
      setError(getResetErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthRecoveryShell
      eyebrow="Sécuriser votre espace"
      title={done ? "Votre accès est restauré" : "Choisissez un nouveau mot de passe"}
      description={done ? "Votre mot de passe a bien été mis à jour. Vous pouvez maintenant retrouver votre espace de calme." : "Choisissez un mot de passe que vous n'utilisez pas ailleurs, puis confirmez-le simplement."}
    >
      {checking && (
        <div
          className="hrec-state"
          aria-live="polite"
        >
          <span className="hrec-loader" />
          <p className="hrec-state-text">Vérification du lien sécurisé…</p>
        </div>
      )}

      {!checking && linkError && (
        <div
          className="hrec-state"
          aria-live="polite"
        >
          <p
            className="hrec-error"
            role="alert"
          >
            {linkError}
          </p>

          <Link
            to="/forgot-password"
            className="hrec-submit"
          >
            Demander un nouveau lien
          </Link>
        </div>
      )}

      {!checking && !linkError && !done && (
        <form
          className="hrec-form"
          onSubmit={handleSubmit}
        >
          {accountEmail && <p className="hrec-note">Compte concerné : {accountEmail}</p>}

          <div className="hrec-group">
            <label
              htmlFor="new-password"
              className="hrec-label"
            >
              Nouveau mot de passe
            </label>

            <div className="hrec-field">
              <LockKeyhole
                size={18}
                className="hrec-field-icon"
              />

              <input
                id="new-password"
                type={showPasswords ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="hrec-input hrec-input--password"
                placeholder="••••••••"
                autoComplete="new-password"
                minLength={6}
                autoFocus
                required
              />

              <button
                type="button"
                className="hrec-toggle"
                onClick={() => setShowPasswords((visible) => !visible)}
                aria-label={showPasswords ? "Masquer les mots de passe" : "Afficher les mots de passe"}
              >
                {showPasswords ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="hrec-group">
            <label
              htmlFor="confirm-password"
              className="hrec-label"
            >
              Confirmer le mot de passe
            </label>

            <div className="hrec-field">
              <LockKeyhole
                size={18}
                className="hrec-field-icon"
              />

              <input
                id="confirm-password"
                type={showPasswords ? "text" : "password"}
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                className="hrec-input hrec-input--password"
                placeholder="••••••••"
                autoComplete="new-password"
                minLength={6}
                required
              />
            </div>
          </div>

          {error && (
            <p
              className="hrec-error"
              role="alert"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            className="hrec-submit"
            disabled={loading}
          >
            {loading && <span className="hrec-spinner" />}
            <span>{loading ? "Mise à jour…" : "Mettre à jour mon mot de passe"}</span>
          </button>

          <p className="hrec-note">6 caractères minimum. Firebase appliquera aussi la politique de sécurité configurée pour votre projet.</p>
        </form>
      )}

      {!checking && !linkError && done && (
        <div
          className="hrec-state"
          aria-live="polite"
        >
          <div className="hrec-state-icon">
            <Check
              size={26}
              strokeWidth={2.2}
            />
          </div>

          <h2 className="hrec-state-title">Tout est prêt</h2>

          <p className="hrec-state-text">Votre nouveau mot de passe est enregistré. Vous pouvez vous reconnecter sereinement.</p>

          <Link
            to="/auth/login"
            className="hrec-submit"
          >
            Se connecter
          </Link>
        </div>
      )}
    </AuthRecoveryShell>
  );
}
